import React, { useState } from 'react';
import { formatFilterPrice, Icon, IconEnum } from '@investoro/core';
import styled from 'styled-components';
import type { MarketProofSectionDto } from 'utils/types/AiPredictorModels';

import PropertyMap from 'components/common/PropertyMap';
import { SectionCard, SectionHeader, SectionTitle, TextContent } from 'components/valuation/common';

const TableWrapper = styled.div`
    width: 100%;
    overflow-x: auto;
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
    font-size: var(--font-size-body-2);

    @media (max-width: 768px) {
        display: block;

        thead {
            display: none;
        }

        tbody {
            display: block;
        }
    }
`;

const Th = styled.th<{ width?: string }>`
    text-align: left;
    padding: 8px 12px;
    font-size: var(--font-size-body-3);
    font-weight: 600;
    color: var(--color-primary-70);
    border-bottom: 2px solid var(--color-alt-second);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: ${({ width }) => width ?? 'auto'};

    @media (min-width: 769px) and (max-width: 1100px) {
        padding: 6px 7px;
        font-size: 11px;
    }
`;

const Td = styled.td<{ subject?: boolean }>`
    padding: 9px 12px;
    font-size: var(--font-size-body-2);
    color: var(--color-primary);
    border-bottom: 1px solid var(--color-alt-second);
    font-weight: ${({ subject }) => subject ? 700 : 500};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    @media (min-width: 769px) and (max-width: 1100px) {
        padding: 7px 7px;
        font-size: 12px;
    }

    @media (max-width: 768px) {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        white-space: normal;
        padding: 8px 12px;
        border-bottom: none;

        &:first-child {
            grid-column: 1 / -1;
            border-bottom: 1px solid var(--color-alt-second);
        }

        &:last-child {
            border-bottom: none;
        }

        &::before {
            content: attr(data-label);
            font-size: var(--font-size-body-3);
            font-weight: 600;
            color: var(--color-primary-70);
            margin-bottom: 2px;
        }
    }
`;

const SubjectTr = styled.tr`
    background: var(--background-color-ai);

    @media (max-width: 768px) {
        display: grid;
        grid-template-columns: 1fr 1fr;
        border: 1px solid var(--color-alt-second);
        border-radius: var(--box-border-radius);
        margin-bottom: 12px;
        overflow: hidden;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.07);
        background: var(--background-color-ai);
    }
`;

const NormalTr = styled.tr`
    &:hover {
        background: var(--color-alt-second);
    }

    @media (max-width: 768px) {
        display: grid;
        grid-template-columns: 1fr 1fr;
        border: 1px solid var(--color-alt-second);
        border-radius: var(--box-border-radius);
        margin-bottom: 12px;
        overflow: hidden;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.07);

        &:hover {
            background: none;
        }
    }
`;

const AddressTd = styled.td<{ subject?: boolean }>`
    padding: 9px 12px;
    font-size: var(--font-size-body-2);
    color: var(--color-primary);
    border-bottom: 1px solid var(--color-alt-second);
    font-weight: ${({ subject }) => subject ? 700 : 500};
    width: 22%;
    white-space: normal;
    word-break: break-word;

    @media (min-width: 769px) and (max-width: 1100px) {
        padding: 7px 7px;
        font-size: 12px;
    }

    @media (max-width: 768px) {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        padding: 8px 12px;
        border-bottom: 1px solid var(--color-alt-second);
        grid-column: 1 / -1;
        width: auto;
        word-break: normal;

        &::before {
            content: attr(data-label);
            font-size: var(--font-size-body-3);
            font-weight: 600;
            color: var(--color-primary-70);
            margin-bottom: 2px;
        }
    }
`;

const OfferLink = styled.a`
    white-space: nowrap;
    font-size: 12px;
    color: var(--color-primary-70);
    text-decoration: none;

    &:hover {
        color: var(--color-alt);
        text-decoration: underline;
    }
`;

const ShowMoreButton = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: 12px;
    background: none;
    border: none;
    cursor: pointer;
    font-size: var(--font-size-body-3);
    font-weight: 600;
    color: var(--color-primary-70);
    padding: 4px 0;

    &:hover {
        color: var(--color-alt);
        text-decoration: underline;
    }
`;

const MapImage = styled.img`
    width: 100%;
    height: auto;
    border-radius: var(--box-border-radius);
    display: block;
`;

const AddressCell = ({ address, subject }: { address?: string | null; subject?: boolean }) => <AddressTd subject={subject} data-label="Adres">
    {address || '—'}
</AddressTd>;
interface ValuationMarketProofProps {
    section: MarketProofSectionDto;
    mapImageUrl?: string;
    hideMap?: boolean;
    hideTable?: boolean;
    tableOnly?: boolean;
    isPdf?: boolean;
    maxRows?: number;
}

const ValuationMarketProof = ({ section, mapImageUrl, hideMap = false, hideTable = false, tableOnly = false, isPdf = false, maxRows }: ValuationMarketProofProps) => {
    const hasContent = section.tableOffers?.length || section.mapPins?.length;

    const [showAdditional, setShowAdditional] = useState(false);

    if (!hasContent) return null;

    const renderTable = (offers = section.tableOffers) => {
        if (!offers?.length) return null;

        return (
            <TableWrapper>
                <Table>
                    <thead>
                        <tr>
                            <Th width="22%">Adres</Th>
                            <Th width="7%">Metraż</Th>
                            <Th width="7%">Pokoje</Th>
                            <Th width="7%">Piętro</Th>
                            <Th width="8%">Rok budowy</Th>
                            <Th width="10%">Cena / m²</Th>
                            <Th width="11%">Cena całkowita</Th>
                            <Th width="9%">Dni od modyf.</Th>
                            {!isPdf && <Th width="6%" />}
                        </tr>
                    </thead>
                    <tbody>
                        {(maxRows ? offers.slice(0, maxRows) : offers).map((row) => {
                            const RowTag = row.isSubjectProperty ? SubjectTr : NormalTr;

                            return (
                                <RowTag key={row.encryptedId ?? row.address}>
                                    <AddressCell address={row.address} subject={row.isSubjectProperty} />
                                    <Td subject={row.isSubjectProperty} data-label="Metraż">{row.area != null ? `${row.area} m²` : '—'}</Td>
                                    <Td subject={row.isSubjectProperty} data-label="Pokoje">{row.numberOfRooms != null ? row.numberOfRooms : '—'}</Td>
                                    <Td subject={row.isSubjectProperty} data-label="Piętro">{row.floor || '—'}</Td>
                                    <Td subject={row.isSubjectProperty} data-label="Rok budowy">{row.builtYear || '—'}</Td>
                                    <Td subject={row.isSubjectProperty} data-label="Cena / m²">{row.pricePerSqm != null ? `${formatFilterPrice(row.pricePerSqm)} zł` : '—'}</Td>
                                    <Td subject={row.isSubjectProperty} data-label="Cena całkowita">{row.totalPrice != null ? `${formatFilterPrice(row.totalPrice)} zł` : '—'}</Td>
                                    <Td subject={row.isSubjectProperty} data-label="Aktualność">{row.dayFromLastModification != null ? `${row.dayFromLastModification} dni` : '—'}</Td>
                                    {!isPdf &&
                                        <Td subject={row.isSubjectProperty} data-label="Oferta">
                                            {row.encryptedId &&
                                                <OfferLink
                                                    href={`${window.location.origin}/sale/flat/${row.encryptedId}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    Zobacz →
                                                </OfferLink>}
                                        </Td>}
                                </RowTag>
                            );
                        })}
                    </tbody>
                </Table>
            </TableWrapper>
        );
    };

    if (tableOnly) {
        return renderTable();
    }

    const renderMap = () => {
        if (hideMap) return null;
        if (mapImageUrl) return <MapImage src={mapImageUrl} alt="Mapa ofert" />;
        if (section.mapPins && section.mapPins.length > 0) return <PropertyMap mapPins={section.mapPins} tableOffers={section.tableOffers} customHeight={isPdf ? '850px' : undefined} />;

        return null;
    };

    return (
        <SectionCard>
            <SectionHeader>
                <Icon icon={IconEnum.CHART} />
                <SectionTitle>Spis ofert w okolicy</SectionTitle>
            </SectionHeader>
            {section.sectionDescription &&
                <TextContent>{section.sectionDescription}</TextContent>}
            {renderMap()}
            {!hideTable && renderTable()}
            {!hideTable && !isPdf && section.additionalTableOffers && section.additionalTableOffers.length > 0 &&
                <>
                    <ShowMoreButton onClick={() => setShowAdditional((prev) => !prev)}>
                        {showAdditional
                            ? 'Ukryj dodatkowe oferty ↑'
                            : `Pokaż ${section.additionalTableOffers.length} więcej ofert ↓`}
                    </ShowMoreButton>
                    {showAdditional &&
                        <>
                            {renderTable(section.additionalTableOffers)}
                            <ShowMoreButton onClick={() => setShowAdditional(false)}>
                                Ukryj dodatkowe oferty ↑
                            </ShowMoreButton>
                        </>}
                </>
            }

        </SectionCard>
    );
};

export default ValuationMarketProof;
